/**
 * WVB-03 — Event Budget Approvals Tab
 * Shows approval workflow state, actions, and history for a submitted budget.
 *
 * ADR-B12 (B3 extension):
 *   SUBMITTED → APPROVED / REJECTED / CHANGES_REQUIRED
 *   APPROVED → SUPERSEDED (via supersede action)
 *   CHANGES_REQUIRED → DRAFT (via revise)
 *
 * Role-scoped views:
 *   - Submitter (PM/CRM): sees status + waiting notice
 *   - Approver (FA): sees Approve / Reject / Request Changes buttons
 *   - Viewer: sees status only
 */
import { useState, useEffect, useCallback } from "react";
import {
  Loader2, CheckCircle2, XCircle, AlertTriangle, Clock, MessageSquare,
  ChevronDown, ChevronUp, User, Shield, RefreshCw,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { budgetApi, approvalApi, hasCapability } from "@/services/api";
import { useAuth } from "@/contexts/AuthContext";
import { BUDGET_STATUS_CONFIG } from "./EventFinancialsUtils";

function fmt(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
}

function HistoryTimeline({ history }) {
  const [open, setOpen] = useState(false);
  if (!history?.length) return null;
  const icons = {
    SUBMITTED: <Clock className="w-3.5 h-3.5 text-blue-500" />,
    APPROVED: <CheckCircle2 className="w-3.5 h-3.5 text-green-500" />,
    REJECTED: <XCircle className="w-3.5 h-3.5 text-red-500" />,
    CHANGES_REQUIRED: <MessageSquare className="w-3.5 h-3.5 text-amber-500" />,
    WITHDRAWN: <RefreshCw className="w-3.5 h-3.5 text-slate-400" />,
    SUPERSEDED: <RefreshCw className="w-3.5 h-3.5 text-purple-400" />,
  };
  return (
    <div className="mt-4">
      <button
        onClick={() => setOpen(v => !v)}
        className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-slate-600 transition-colors"
      >
        {open ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
        Decision History ({history.length})
      </button>
      {open && (
        <div className="mt-3 space-y-2 pl-2 border-l-2 border-slate-100">
          {history.map((h, i) => (
            <div key={i} className="flex items-start gap-2 text-xs">
              <span className="mt-0.5">{icons[h.action] || <Clock className="w-3.5 h-3.5 text-slate-400" />}</span>
              <div>
                <span className="font-semibold text-slate-700">{h.action}</span>
                <span className="text-slate-400 ml-1">· {fmt(h.timestamp)}</span>
                {h.comments && <p className="text-slate-500 mt-0.5 italic">"{h.comments}"</p>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ApprovePanel({ eid, bid, rid, onDone }) {
  const [comments, setComments] = useState("");
  const [loading, setLoading] = useState(false);

  async function handleApprove() {
    setLoading(true);
    try {
      await approvalApi.approve(eid, bid, rid, { comments });
      toast.success("Budget approved");
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Approval failed");
    } finally { setLoading(false); }
  }

  return (
    <div className="bg-green-50 border border-green-200 rounded-xl p-4 space-y-3" data-testid="approve-panel">
      <p className="text-sm font-semibold text-green-800">Approve Budget</p>
      <textarea
        value={comments} onChange={e => setComments(e.target.value)}
        placeholder="Optional approval comments"
        rows={2}
        className="w-full text-sm border border-green-200 rounded-lg px-3 py-2 text-slate-700 resize-none focus:outline-none focus:ring-1 focus:ring-green-400"
        data-testid="approve-comments-input"
      />
      <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white" onClick={handleApprove} disabled={loading} data-testid="confirm-approve-btn">
        {loading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <CheckCircle2 className="w-4 h-4 mr-1" />}
        Confirm Approval
      </Button>
    </div>
  );
}

function RejectPanel({ eid, bid, rid, onDone }) {
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(false);

  async function handleReject() {
    if (!reason.trim() || reason.trim().length < 5) {
      toast.error("A rejection reason is required (min 5 characters)");
      return;
    }
    setLoading(true);
    try {
      await approvalApi.reject(eid, bid, rid, { reason, comments: null });
      toast.success("Budget rejected");
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Rejection failed");
    } finally { setLoading(false); }
  }

  return (
    <div className="bg-red-50 border border-red-200 rounded-xl p-4 space-y-3" data-testid="reject-panel">
      <p className="text-sm font-semibold text-red-800">Reject Budget</p>
      <textarea
        value={reason} onChange={e => setReason(e.target.value)}
        placeholder="Documented rejection reason (required)"
        rows={2}
        className="w-full text-sm border border-red-200 rounded-lg px-3 py-2 text-slate-700 resize-none focus:outline-none focus:ring-1 focus:ring-red-400"
        data-testid="reject-reason-input"
      />
      <Button size="sm" variant="destructive" onClick={handleReject} disabled={loading} data-testid="confirm-reject-btn">
        {loading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <XCircle className="w-4 h-4 mr-1" />}
        Confirm Rejection
      </Button>
    </div>
  );
}

function ChangesRequiredPanel({ eid, bid, rid, onDone }) {
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(false);

  async function handleRequestChanges() {
    if (!reason.trim() || reason.trim().length < 10) {
      toast.error("A documented reason for changes is required (min 10 characters)");
      return;
    }
    setLoading(true);
    try {
      await approvalApi.requestChanges(eid, bid, rid, { reason, comments: null });
      toast.success("Changes requested — submitter will be notified");
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Request failed");
    } finally { setLoading(false); }
  }

  return (
    <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 space-y-3" data-testid="changes-required-panel">
      <p className="text-sm font-semibold text-amber-800">Request Changes</p>
      <textarea
        value={reason} onChange={e => setReason(e.target.value)}
        placeholder="Specific changes required (mandatory — at least 10 characters)"
        rows={3}
        className="w-full text-sm border border-amber-200 rounded-lg px-3 py-2 text-slate-700 resize-none focus:outline-none focus:ring-1 focus:ring-amber-400"
        data-testid="changes-reason-input"
      />
      <Button size="sm" className="bg-amber-600 hover:bg-amber-700 text-white" onClick={handleRequestChanges} disabled={loading} data-testid="confirm-changes-btn">
        {loading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <MessageSquare className="w-4 h-4 mr-1" />}
        Send to Submitter
      </Button>
    </div>
  );
}

export default function EventBudgetApprovalsTab({ eid, budget, onRefresh }) {
  const { user } = useAuth();
  const [approvalData, setApprovalData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeAction, setActiveAction] = useState(null); // "approve"|"reject"|"changes"
  const [revising, setRevising] = useState(false);

  const canApprove = hasCapability(user, "budget.approve");
  const canReject = hasCapability(user, "budget.reject");
  const canRequestChanges = hasCapability(user, "budget.request_changes");
  const canEdit = hasCapability(user, "budget.edit");
  const canSupersede = hasCapability(user, "budget.supersede");

  const bid = budget?.id;

  const loadApproval = useCallback(async () => {
    if (!bid) { setLoading(false); return; }
    try {
      const res = await approvalApi.getApprovalRequest(eid, bid);
      setApprovalData(res.data);
    } catch { setApprovalData(null); }
    finally { setLoading(false); }
  }, [eid, bid]);

  useEffect(() => { loadApproval(); }, [loadApproval]);

  async function handleRevise() {
    setRevising(true);
    try {
      await budgetApi.reviseBudget(eid, bid);
      toast.success("Budget returned to DRAFT for revision");
      await onRefresh();
      await loadApproval();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Revise failed");
    } finally { setRevising(false); }
  }

  function handleActionDone() {
    setActiveAction(null);
    loadApproval();
    onRefresh();
  }

  if (loading) return <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-indigo-400" /></div>;

  if (!budget) {
    return (
      <div className="flex flex-col items-center py-16 gap-2 text-slate-400" data-testid="no-budget-approvals">
        <Shield className="w-10 h-10 text-slate-200" />
        <p className="text-sm">No budget to review — create a budget first.</p>
      </div>
    );
  }

  const budgetStatus = budget.status;
  const statusCfg = BUDGET_STATUS_CONFIG[budgetStatus] || { cls: "bg-slate-100 text-slate-500", label: budgetStatus };
  const approvalReq = approvalData?.approval_request;
  const rid = approvalReq?.id;

  // Determine if current user is the submitter
  const isSubmitter = approvalReq?.submitted_by_user_id === user?.id;
  // SoD: approver cannot be submitter
  const canActAsApprover = !isSubmitter && (canApprove || canReject || canRequestChanges);

  return (
    <div className="space-y-4" data-testid="approvals-tab">

      {/* Budget identity */}
      <div className="bg-white rounded-xl border border-slate-200 p-4 flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <Shield className="w-4 h-4 text-indigo-500" />
          <span className="font-bold text-slate-900">{budget.budget_number}</span>
          <span className="text-slate-400 text-sm">v{budget.budget_version}</span>
          <span className={`text-xs px-2.5 py-0.5 rounded-full font-semibold ${statusCfg.cls}`}>{statusCfg.label}</span>
        </div>
        <div className="text-sm text-slate-500">
          {budget.total_forecast_display || "—"}
          <span className="text-slate-300 mx-1">·</span>
          {budget.currency_code}
        </div>
      </div>

      {/* ── DRAFT state ── */}
      {budgetStatus === "DRAFT" && (
        <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-sm text-slate-500" data-testid="draft-state-notice">
          <Shield className="w-4 h-4 inline mr-1.5 text-slate-300" />
          Budget is in DRAFT — submit it from the Overview tab to begin the approval process.
        </div>
      )}

      {/* ── PENDING REVIEW state ── */}
      {budgetStatus === "SUBMITTED" && approvalReq && (
        <div className="space-y-3" data-testid="pending-review-state">
          {/* Approval request card */}
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 space-y-2">
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-blue-500" />
              <span className="font-semibold text-blue-800">{approvalReq.approval_number}</span>
              <span className="text-xs bg-blue-100 text-blue-600 px-2 py-0.5 rounded-full">Pending Review</span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-xs">
              <div>
                <span className="text-slate-400">Submitted by </span>
                <span className="text-slate-700">{approvalReq.submitted_by_email}</span>
              </div>
              <div>
                <span className="text-slate-400">Submitted on </span>
                <span className="text-slate-700">{fmt(approvalReq.submitted_at)}</span>
              </div>
              {approvalReq.policy_snapshot && (
                <div>
                  <span className="text-slate-400">Policy </span>
                  <span className="text-slate-700">{approvalReq.policy_snapshot.name || approvalReq.policy_id}</span>
                </div>
              )}
            </div>
          </div>

          {/* SoD notice for approver who is also submitter */}
          {isSubmitter && (canApprove || canReject) && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-sm text-amber-700" data-testid="sod-warning">
              <AlertTriangle className="w-4 h-4 inline mr-1.5" />
              <span className="font-semibold">Segregation of Duties:</span> You submitted this budget. A different Finance Approver must review it.
            </div>
          )}

          {/* Approver action buttons */}
          {canActAsApprover && activeAction === null && (
            <div className="flex flex-wrap gap-2" data-testid="approver-action-buttons">
              {canApprove && (
                <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white" onClick={() => setActiveAction("approve")} data-testid="approve-btn">
                  <CheckCircle2 className="w-4 h-4 mr-1.5" /> Approve
                </Button>
              )}
              {canRequestChanges && (
                <Button size="sm" className="bg-amber-500 hover:bg-amber-600 text-white" onClick={() => setActiveAction("changes")} data-testid="request-changes-btn">
                  <MessageSquare className="w-4 h-4 mr-1.5" /> Request Changes
                </Button>
              )}
              {canReject && (
                <Button size="sm" variant="destructive" onClick={() => setActiveAction("reject")} data-testid="reject-btn">
                  <XCircle className="w-4 h-4 mr-1.5" /> Reject
                </Button>
              )}
            </div>
          )}

          {/* Active action panels */}
          {activeAction === "approve" && <ApprovePanel eid={eid} bid={bid} rid={rid} onDone={handleActionDone} />}
          {activeAction === "reject" && <RejectPanel eid={eid} bid={bid} rid={rid} onDone={handleActionDone} />}
          {activeAction === "changes" && <ChangesRequiredPanel eid={eid} bid={bid} rid={rid} onDone={handleActionDone} />}
          {activeAction !== null && (
            <button onClick={() => setActiveAction(null)} className="text-xs text-slate-400 hover:text-slate-600">
              ← Cancel
            </button>
          )}

          {/* Waiting notice for submitter */}
          {isSubmitter && !canActAsApprover && (
            <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 text-sm text-slate-500">
              <User className="w-4 h-4 inline mr-1.5" />
              Waiting for Finance Approver decision.
            </div>
          )}

          <HistoryTimeline history={approvalReq.history} />
        </div>
      )}

      {/* ── APPROVED state ── */}
      {budgetStatus === "APPROVED" && approvalReq && (
        <div className="space-y-3" data-testid="approved-state">
          <div className="bg-green-50 border border-green-200 rounded-xl p-4 space-y-3">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-green-500" />
              <span className="font-bold text-green-800">Budget Approved</span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-xs">
              <div>
                <span className="text-slate-400">Approved by </span>
                <span className="text-green-700 font-medium">{approvalReq.reviewed_by_user_id}</span>
              </div>
              <div>
                <span className="text-slate-400">Approved on </span>
                <span className="text-slate-700">{fmt(approvalReq.reviewed_at)}</span>
              </div>
              <div>
                <span className="text-slate-400">Total </span>
                <span className="font-semibold text-green-700">{approvalReq.approved_baseline?.total_forecast_display}</span>
              </div>
            </div>
            {approvalReq.review_comments && (
              <p className="text-xs text-green-700 bg-green-100 rounded-lg px-3 py-2 italic">
                "{approvalReq.review_comments}"
              </p>
            )}
            <p className="text-xs text-green-600 bg-green-100 rounded-lg px-3 py-1.5">
              Approved baseline is immutable. Use Supersede to begin a revision.
            </p>
          </div>

          {canSupersede && (
            <Button size="sm" variant="outline" onClick={async () => {
              try {
                await approvalApi.supersede(eid, bid);
                toast.success("Budget superseded — new DRAFT created");
                onRefresh();
              } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
            }} data-testid="supersede-btn">
              <RefreshCw className="w-4 h-4 mr-1.5" /> Supersede & Revise
            </Button>
          )}

          <HistoryTimeline history={approvalReq.history} />
        </div>
      )}

      {/* ── REJECTED state ── */}
      {budgetStatus === "REJECTED" && approvalReq && (
        <div className="space-y-3" data-testid="rejected-state">
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 space-y-2">
            <div className="flex items-center gap-2">
              <XCircle className="w-5 h-5 text-red-500" />
              <span className="font-bold text-red-800">Budget Rejected</span>
            </div>
            <p className="text-sm text-red-700"><span className="font-semibold">Reason: </span>{approvalReq.review_reason}</p>
            <div className="text-xs text-slate-500">{fmt(approvalReq.reviewed_at)}</div>
          </div>
          <HistoryTimeline history={approvalReq.history} />
        </div>
      )}

      {/* ── CHANGES_REQUIRED state ── */}
      {budgetStatus === "CHANGES_REQUIRED" && approvalReq && (
        <div className="space-y-3" data-testid="changes-required-state">
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 space-y-2">
            <div className="flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-amber-500" />
              <span className="font-bold text-amber-800">Changes Required</span>
            </div>
            <p className="text-sm text-amber-700"><span className="font-semibold">Required changes: </span>{approvalReq.review_reason}</p>
            {approvalReq.review_comments && (
              <p className="text-xs text-amber-600 italic">"{approvalReq.review_comments}"</p>
            )}
          </div>
          {canEdit && (
            <Button size="sm" onClick={handleRevise} disabled={revising} data-testid="revise-after-changes-btn">
              {revising ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <RefreshCw className="w-4 h-4 mr-1" />}
              Create New Draft Revision
            </Button>
          )}
          <HistoryTimeline history={approvalReq.history} />
        </div>
      )}

      {/* ── No approval request yet (SUBMITTED but no request) ── */}
      {budgetStatus === "SUBMITTED" && !approvalReq && (
        <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-sm text-slate-500" data-testid="no-approval-request">
          <Clock className="w-4 h-4 inline mr-1.5" />
          Budget submitted but no approval request found. Please contact your administrator.
        </div>
      )}
    </div>
  );
}
